import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { OnyxEvent } from '../models/event';
import { BasicEventDataValidator } from './basic-event-data.validator';
import { CustomValidators } from 'ng2-validation';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { DateUtil } from '../../shared/utils/date.util';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { GroupPayConfigStore } from '../service/grouppay-config.store';
import { PropertyLookupDetail } from '../../shared/models/property-lookup-detail';
import { ConfigLookupType } from '../models/config/config-lookup-type';
import { ConfigResponse } from '../models/config/config-response';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { EventStore } from '../service/event.store';
import { Observable } from 'rxjs/Observable';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { BulkUploadResponse } from '../models/bulk-upload-response';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'pioneer-basic-event-data',
  templateUrl: './basic-event-data.component.html'
})
export class BasicEventDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input('parentForm')
  public parentForm: FormGroup;

  public onyxEvent: OnyxEvent;

  @Input()
  public isReadOnly = true;

  @Input()
  public isNewEvent = false;

  @ViewChild('bulkEventFile') bulkEventFileInput: ElementRef;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  fileExtensions: Array<string>;

  isUploadInProgress: boolean;

  public basicEventDataForm: FormGroup;

  private propertyLookupSubscription: Subscription;

  constructor(private formBuilder: FormBuilder,
              private propertyLookupStore: PropertyLookupStore,
              private eventStore: EventStore,
              private environment: EnvironmentConfiguration,
              private groupPayConfigStore: GroupPayConfigStore,
              private flashMessagesService: FlashMessagesService,
              private authenticationStore: AuthenticationStore) {
  }

  private dataToFormGroup(event: OnyxEvent): void {
    let _groupCodeNumber = '';
    if (event.eventProperty) {
      _groupCodeNumber = event.eventProperty.eventReferenceNumber;
    }
    if (!this.basicEventDataForm) {

      this.basicEventDataForm = this.formBuilder.group({
          'name': [ event.name, Validators.required ],
          'commissionRoomPercentage': [ event.commissionRoomPercentage,
            [ Validators.required, CustomValidators.range([ 0, 100 ]), PioneerValidators.isInteger ] ],
          'startDate': [ DateUtil.formatDate(event.startDate), [ Validators.required, PioneerValidators.isDate ] ],
          'endDate': [ DateUtil.formatDate(event.endDate), [ Validators.required, PioneerValidators.isDate ] ],
          'eventAgency': this.formBuilder.group({}),
          'eventClient': this.formBuilder.group({}),
          'eventProperty': this.formBuilder.group({}),
          'groupCodeNumber': [ _groupCodeNumber, Validators.required]
        }, { validator: BasicEventDataValidator.dateCompare }
      );
    } else {

      this.basicEventDataForm.patchValue({
        'name': event.name,
        'commissionRoomPercentage': event.commissionRoomPercentage,
        'startDate': DateUtil.formatDate(event.startDate),
        'endDate': DateUtil.formatDate(event.endDate),
        'groupCodeNumber': _groupCodeNumber
      });
    }
  }

  ngOnInit() {
    this.dataToFormGroup(this.onyxEvent);
    this.parentForm.setControl('event', this.basicEventDataForm);
    this.isUploadInProgress = false;
    this.fileExtensions = this.environment.bulkEvents.acceptedFileExtensions;

    this.propertyLookupSubscription =
      this.propertyLookupStore.selected.subscribe((propertyLookup: PropertyLookupDetail) => {
        this.groupPayConfigStore.loadConfig(ConfigLookupType.COMMISSION_RATE,
          propertyLookup.chain, propertyLookup.brand, propertyLookup.propertyId, null)
            .map((res: ConfigResponse) => Number(res.value))
            .subscribe((rate: number) => {
              if (this.isNewEvent && !this.isCommissionRateManuallyEntered()) {
                this.basicEventDataForm.patchValue({
                  'commissionRoomPercentage': rate
                });
              }
            });
        });
  }

  ngOnDestroy() {
    this.propertyLookupSubscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    /*
     * Since we're subscribed to the selectedEvent in the event store,
     * this will detect if something changes (or if loading finishes) and we need to update the form.
     */
    if (!changes[ 'event' ]) {
      return;
    }

    this.dataToFormGroup(this.onyxEvent);
  }

  isAutoGenerated(): boolean {
    return this.onyxEvent.autoGenerated;
  }

  isCommissionRateManuallyEntered(): boolean {
    return this.basicEventDataForm.controls['commissionRoomPercentage'].touched &&
      this.basicEventDataForm.controls['commissionRoomPercentage'].dirty;
  }

  handleUploadError(errorMessage: string, logMessage: string = null) {
    this.isUploadInProgress = false;
    this.flashMessagesService.error(errorMessage, logMessage);
  }

  clearFileInput(): void {
    this.bulkEventFileInput.nativeElement.value = null;
  }

  downloadInvalidEventsCsv(fileId: number): void {
    this.eventStore.downloadInvalidEventsCsv(fileId);
  }

  importEventsViaCsv(inputEvent) {
    this.isUploadInProgress = true;
    if (!inputEvent || !inputEvent.srcElement || !inputEvent.srcElement.files || inputEvent.srcElement.files.size === 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_BULK_EVENTS');
      return;
    }

    const file = inputEvent.srcElement.files[ 0 ];
    this.clearFileInput();

    if (this.environment.bulkEvents.acceptedFileTypes.indexOf(file.type) < 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_BULK_EVENTS');
      return;
    }
    if (file.size > Math.pow(2, 20)) {
      this.handleUploadError('MESSAGES.ERROR.BULK_UPLOAD_TOO_LARGE');
      return;
    };
    this.eventStore.importEventsViaCsv(file)
      .finally(() => {
        this.isUploadInProgress = false;
      })
      .catch((error => {
        this.showErrorMessage(error);
        return Observable.throw(error);
      }))
      .subscribe((response) => {
        if (response.successful && response.successful > 0) {
          this.showSuccessfulRecordsMessage(response);
        }
        if (response.failed && response.failed > 0) {
          this.showInvalidRecordsMessage(response);
        }
      });
  }

  showErrorMessage(error): void {
    this.flashMessagesService.error('MESSAGES.ERROR.UPLOAD_BULK_EVENTS', `Error uploading bulk events: ${error}`);
  }

  showSuccessfulRecordsMessage(response: BulkUploadResponse): void {
    this.flashMessagesService.success('MESSAGES.SUCCESS.UPLOAD_BULK_EVENTS', null, { eventCount: Number(response.successful) });
  }

  showInvalidRecordsMessage(response: BulkUploadResponse): void {
    this.flashMessagesService.warn('MESSAGES.SUCCESS.UPLOAD_BULK_EVENTS_INVALID', null,

      { eventCount: Number(response.failed),
        href: this.environment.baseUrl + '/api/events/bulkUpload/'
        + response.fileId + '?Authorization=' + this.authenticationStore.getToken()});
  }

  setEndDateAfterStartDateEntered(daysToAdd: number): void {
    const startDate = DateUtil.parseDate(this.basicEventDataForm.controls.startDate.value);
    if (DateUtil.isValidDate(startDate)) {
      const newEndDate = new Date(startDate.setTime(startDate.getTime() + daysToAdd * DateUtil.MILLISECONDS_PER_DAY));
      const dateStr = DateUtil.formatDate(newEndDate);
      this.basicEventDataForm.controls['endDate'].setValue(dateStr);
    }
  }

  onStartDateEntered(inputEvent: Event): void {
    if (inputEvent) {
      this.loadDefaultEndDateDelayDays();
    }
  }

  loadDefaultEndDateDelayDays(): void {
    const startDate = DateUtil.parseDate(this.basicEventDataForm.controls.startDate.value);
    if (DateUtil.isValidDate(startDate)) {
      this.groupPayConfigStore.loadConfig(ConfigLookupType.DEFAULT_END_DATE_PLUS_DAYS, null, null, null, null)
        .map((res: ConfigResponse) => Number(res.value))
        .subscribe((daysToAdd: number) => {
          this.setEndDateAfterStartDateEntered(daysToAdd);
        });
    }
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/basic-event-data/basic-event-data.component.ts